import { ICellEditorParams } from '@ag-grid-community/core'
import { ICellEditorAngularComp } from '@ag-grid-community/angular'
import { Component, ElementRef, ViewChild } from '@angular/core'
import { BulkCellKind } from '../../bulk-transaction/bulk-transaction.projection'
import { PICKER_TRIGGER_CLASS } from '../value-picker/value-picker'

/** What the grid hands the editor, beyond ag-grid's own params. */
export interface BulkCellEditorParams extends ICellEditorParams {
  kind: BulkCellKind
  /** The stored value in the form it is TYPED in, which is not the form it is read in. */
  editValue: string
  /**
   * Told of every keystroke in a LOOKUP cell, with the cell the suggestions hang off (PVAL-26685).
   *
   * The editor only reports the typing: asking the endpoint, debouncing it and drawing the answers
   * belong to the dialog, which owns the panel. Absent for every other kind — a number or a
   * free-text cell has nothing to ask.
   */
  onType?: (text: string, anchor: HTMLElement) => void
}

/** The key `frameworkComponents` registers this editor under. */
export const BULK_CELL_EDITOR = 'bulkCellEditor'

/**
 * The editor for a bulk cell — one component for number, lookup and free text.
 *
 * One rather than three because they differ in a placeholder and nothing else. The behaviour that
 * matters is shared — a cell is READ in its Display Style (`1,250.00`) and TYPED in a form
 * `rawValueFrom` accepts, so arming reveals the typed form and committing settles it back. That is
 * the same arm/commit cycle the single-record dialog runs; here ag-grid drives it, calling `agInit`
 * on arm and `getValue` on commit.
 *
 * A DATE does not come here: it uses Northfork's inline-edit column type, the same one Cash Entry
 * and Trade Entry use, whose editor is `nf-datepicker` — see `inlineDateColumn`.
 *
 * The enum has its own editor — it opens an anchored panel rather than accepting typing — and lands
 * in the next phase.
 */
@Component({
  selector: 'hs-bulk-cell-editor',
  template: `
    <input
      #input
      class="bt-ag-editor"
      type="text"
      [attr.placeholder]="placeholder"
      [value]="value"
      (input)="onInput(input.value)"
    />
  `,
  styleUrls: ['./bulk-cell-editor.component.scss'],
})
export class BulkCellEditorComponent implements ICellEditorAngularComp {
  @ViewChild('input', { static: true })
  private readonly input!: ElementRef<HTMLInputElement>

  value = ''
  placeholder: string | null = null

  /** The lookup's keystroke hook and the cell it reports against; both unset for any other kind. */
  private onType: BulkCellEditorParams['onType']
  private anchor: HTMLElement | null = null

  agInit(params: BulkCellEditorParams): void {
    // The form this kind is TYPED in, which the column decides: a pretty number reads `1,250.00`
    // and is typed `1250`.
    this.value = params.editValue ?? ''
    this.placeholder = params.kind === 'lookup' ? 'Type to search' : null
    this.onType = params.onType
    this.anchor = params.eGridCell ?? null
    // The value panel's dismissal rule spares a press on its trigger, and a lookup's trigger is the
    // box itself: a click to move the caret must not shut the answers it is typing against.
    if (params.kind === 'lookup') {
      this.input.nativeElement.classList.add(PICKER_TRIGGER_CLASS)
    }
  }

  onInput(text: string): void {
    this.value = text
    if (this.anchor) this.onType?.(text, this.anchor)
  }

  /** What ag-grid writes back. The column's `valueSetter` is where it becomes a stored value. */
  getValue(): string {
    return this.value
  }

  /** Inline, not a popup: the cell is the box, exactly as the design draws it. */
  isPopup(): boolean {
    return false
  }

  afterGuiAttached(): void {
    const element = this.input.nativeElement
    element.focus()
    element.select()
  }
}
